import React from 'react';
import { CalendarPlus, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';

interface AddToCalendarButtonProps {
  title: string;
  description?: string;
  location?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm (24h)
  durationMinutes?: number; // default 60
  timezone?: string; // Prefer IANA (e.g. Asia/Kolkata). Fallbacks handled for IST/EST
  className?: string;
}

const mapToIanaTz = (tz?: string) => {
  if (!tz) return undefined;
  const upper = tz.toUpperCase();
  if (upper === 'IST') return 'Asia/Kolkata';
  if (upper === 'EST' || upper === 'EDT' || upper === 'ET') return 'America/New_York';
  if (tz.includes('/')) return tz; // assume already IANA
  return undefined;
};

const pad = (n: number) => n.toString().padStart(2, '0');

const formatCompact = (d: Date) => {
  // YYYYMMDDTHHMMSS
  return (
    d.getFullYear().toString() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    'T' +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds())
  );
};

const toLocalDateFromParts = (date: string, time: string) => {
  // Constructs a Date in local timezone using provided parts
  // date: YYYY-MM-DD, time: HH:mm
  return new Date(`${date}T${time}:00`);
};

const addMinutes = (d: Date, minutes: number) => new Date(d.getTime() + minutes * 60 * 1000);

const escapeIcsText = (text: string) =>
  text
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

export const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({
  title,
  description,
  location,
  date,
  time,
  durationMinutes = 60,
  timezone,
  className
}) => {
  const tzIana = mapToIanaTz(timezone);

  // We avoid complex timezone math by using Google Calendar's `ctz` param
  // and local times for the compact format. For ICS, we include TZID when available.
  const startLocal = toLocalDateFromParts(date, time);
  const endLocal = addMinutes(startLocal, durationMinutes);

  const startStr = formatCompact(startLocal);
  const endStr = formatCompact(endLocal);

  const handleGoogle = () => {
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${startStr}/${endStr}`,
      details: description || '',
      location: location || ''
    });
    if (tzIana) params.set('ctz', tzIana);
    const href = `https://calendar.google.com/calendar/render?${params.toString()}`;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const handleIcs = () => {
    const nowUtc = new Date();
    const dtStamp = new Date(nowUtc.getTime() - nowUtc.getTimezoneOffset() * 60000);
    const dtStampStr = formatCompact(dtStamp) + 'Z';

    const uid = `${Date.now()}@cutshort.ai`;

    const lines: string[] = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Cutshort//Events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtStampStr}`,
    ];

    if (tzIana) {
      lines.push(`DTSTART;TZID=${tzIana}:${startStr}`);
      lines.push(`DTEND;TZID=${tzIana}:${endStr}`);
    } else {
      // Fallback: export as local time without TZ (some clients interpret as local)
      lines.push(`DTSTART:${startStr}`);
      lines.push(`DTEND:${endStr}`);
    }

    lines.push(`SUMMARY:${escapeIcsText(title)}`);
    if (description) lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
    if (location) lines.push(`LOCATION:${escapeIcsText(location)}`);
    lines.push('END:VEVENT', 'END:VCALENDAR');

    const icsContent = lines.join('\r\n');
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeTitle = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    a.href = url;
    a.download = `${safeTitle || 'event'}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={className}>
          <CalendarPlus className="h-4 w-4 mr-2" />
          Add to calendar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={handleGoogle}>
          <CalendarPlus className="h-4 w-4 mr-2" /> Google Calendar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleIcs}>
          <Download className="h-4 w-4 mr-2" /> Download .ics
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToCalendarButton;



